import React from 'react';
import { useParams, Switch, Route, useHistory } from 'react-router-dom';
import TicketOffice from '../../models/ticket_office';
import Template from '../template/template';
import TicketOfficeEvent from '../../pages/ticket_offices/detail/events/ticketOfficeEvent';
import TicketOfficeOperator from '../../pages/ticket_offices/detail/operators/ticketOfficeOperator';
import TicketOfficeEventRates from '../../pages/ticket_offices/detail/events/rates/ticketOfficeEventRates';
import Loading from '../loading';
import TicketOfficeParameters from '../../pages/ticket_offices/detail/settings/ticketOfficeParameters';
import OperatorCreate from '../../pages/ticket_offices/detail/operators/operatorCreation';
import Home from '../../pages/ticket_offices/detail/home/home';
import SalesReport from '../../pages/ticket_offices/detail/report/sales/salesReport';
import CheckoutReport from '../../pages/ticket_offices/detail/report/checkout/checkoutReport';
import { ManagerContextProvider } from '../../pages/manager/components/context';
import OperatorEdit from '../../pages/ticket_offices/detail/operators/operatorEdit';
import PageError from '../../pages/error/pageError';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

export const ticketOfficeContext = React.createContext();

export default function TicketOfficeRouter({ organization, path }) {
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const { t } = useTranslation();
    const { ticket_office_id } = useParams();

    const [ticketsOffice, loadingTicketOffice, syncTicketOffice] = TicketOffice.useApiModel(
        {
            query: 'expand=forms',
            organizationId: organization.id,
            id: ticket_office_id,
            cache: false,
            allow_cache: false,
            onError: e => {
                if (e.code === 404) {
                    toast.error(t('pageNotFound'));
                } else {
                    toast.error(t('ticket.common.toast_error'));
                }

                history.push(`/ticket/O${organization.id}/ticket_offices`);
            }
        },
        [organization, ticket_office_id]
    );

    const hasModerationEnabled = React.useMemo(() => {
        if (!ticketsOffice) {
            return false;
        }
        return ticketsOffice.events.some(ev => ev.is_moderation_enabled);
    }, [ticketsOffice]);

    if (loadingTicketOffice || !ticketsOffice) {
        return <Loading />;
    }

    return (
        <ManagerContextProvider>
            <ticketOfficeContext.Provider value={syncTicketOffice}>
                <Template organization={organization} ticket_office={ticketsOffice} syncTicketOffice={syncTicketOffice}>
                    <Switch>
                        <Route
                            exact
                            path={`${path}/dashboard`}
                            render={({ match, history }) => {
                                return <Home organization={organization} ticket_office={ticketsOffice} match={match} path={path} />;
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/events`}
                            render={({ match, history }) => {
                                return <TicketOfficeEvent organization={organization} ticket_office={ticketsOffice} syncTicketOffice={syncTicketOffice} />;
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/events/:eventId`}
                            render={({ match, history }) => {
                                return (
                                    <TicketOfficeEventRates
                                        organization={organization}
                                        ticket_office={ticketsOffice}
                                        eventId={match.params.eventId}
                                        syncTicketOffice={syncTicketOffice}
                                    />
                                );
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/settings`}
                            render={({ match, history }) => {
                                return (
                                    <TicketOfficeParameters
                                        organization={organization}
                                        ticket_office={ticketsOffice}
                                        syncTicketOffice={syncTicketOffice}
                                        hasModerationEnabled={hasModerationEnabled}
                                    />
                                );
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/users`}
                            render={({ match, history }) => {
                                return <TicketOfficeOperator organization={organization} ticket_office={ticketsOffice} syncTicketOffice={syncTicketOffice} />;
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/users/new`}
                            render={({ match, history }) => {
                                return <OperatorCreate organization={organization} ticket_office={ticketsOffice} />;
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/users/:userId`}
                            render={({ match, history }) => {
                                return <OperatorEdit organization={organization} ticket_office={ticketsOffice} operator_id={match.params.userId} />;
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/reports/salesjournal`}
                            render={() => {
                                return <SalesReport organization={organization} ticket_office={ticketsOffice} />;
                            }}
                        />
                        <Route
                            exact
                            path={`${path}/reports/checkout`}
                            render={() => {
                                return <CheckoutReport organization={organization} ticket_office={ticketsOffice} />;
                            }}
                        />
                        {/* Error */}
                        <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/ticket_offices/${ticket_office_id}/dashboard`} />} />
                    </Switch>
                </Template>
            </ticketOfficeContext.Provider>
        </ManagerContextProvider>
    );
}
